/**
 * BIRTHDAY PROMO — SINGLE SOURCE OF TRUTH (Aureus Birthday 2026)
 *
 * This is a plain ESM JS module so BOTH the Vite frontend and the Node API
 * (api/**) can import the exact same values without drift. constants.ts
 * re-exports these for frontend consumers (constants.ts itself uses
 * import.meta.env which the Node backend cannot import).
 *
 * Auto-expiry: after endDate, isBirthdayPromoActive() returns false — banner,
 * package buttons, and bonus logic all go inert with no code change required.
 *
 * SAFETY: gift issuance rows created from this config MUST use
 * shares_purchased=0 and total_amount=0 so the aureus_share_purchases INSERT
 * triggers (team-volume credit guarded by total_amount<=0, pool ledger guarded
 * by total_amount>0.20) do not fire on commission-free gift rows.
 */

/** @typedef {'BIRTHDAY_2'|'BIRTHDAY_25'} BirthdayPackageId */

export const BIRTHDAY_PROMO_CONFIG = {
  enabled: true,
  startDate: '2026-07-25T00:00:00Z',
  endDate: '2026-08-06T23:59:59Z', // inclusive of 2026-08-06
  // Quantity multiplier: buyer may purchase N units of a package (linear scaling).
  minQty: 1,
  maxQty: 20,
  packages: {
    BIRTHDAY_2: { id: 'BIRTHDAY_2', label: 'Anniversary Package 1', buyShares: 2, buyerBonus: 1, uplineBonus: 1 },
    BIRTHDAY_25: { id: 'BIRTHDAY_25', label: 'Anniversary Package 2', buyShares: 25, buyerBonus: 25, uplineBonus: 12.5 },
  },
  markers: {
    buyerGift: 'BIRTHDAY_2026_BUYER_GIFT',
    uplineGift: 'BIRTHDAY_2026_UPLINE_GIFT',
    presaleGift: 'BIRTHDAY_2026_PRESALE_GIFT',
  },
  presaleGrantShares: 3,
};

/**
 * Whether the birthday promo is currently active (date-gated + enabled flag).
 * @param {Date} [now]
 * @returns {boolean}
 */
export function isBirthdayPromoActive(now = new Date()) {
  if (!BIRTHDAY_PROMO_CONFIG.enabled) return false;
  const t = now.getTime();
  return (
    t >= Date.parse(BIRTHDAY_PROMO_CONFIG.startDate) &&
    t <= Date.parse(BIRTHDAY_PROMO_CONFIG.endDate)
  );
}

/**
 * Type guard for a valid birthday package id.
 * @param {unknown} id
 * @returns {id is BirthdayPackageId}
 */
export function isBirthdayPackageId(id) {
  return id === 'BIRTHDAY_2' || id === 'BIRTHDAY_25';
}

/**
 * Clamp/normalize a requested package quantity to [minQty, maxQty] whole units.
 * Returns null if the input is not a positive finite number.
 * @param {unknown} qty
 * @returns {number|null}
 */
export function normalizeBirthdayQty(qty) {
  const n = Number(qty);
  if (!Number.isFinite(n)) return null;
  const whole = Math.floor(n);
  if (whole < BIRTHDAY_PROMO_CONFIG.minQty) return null;
  return Math.min(whole, BIRTHDAY_PROMO_CONFIG.maxQty);
}

/**
 * Compute the linearly-scaled share breakdown for a package purchased at a
 * given quantity. All values scale per unit:
 *   baseShares  = buyShares  * qty  (buyer PAYS for these)
 *   buyerBonus  = buyerBonus * qty  (commission-free gift to buyer)
 *   uplineBonus = uplineBonus* qty  (commission-free gift to level-1 sponsor)
 *
 * @param {BirthdayPackageId} packageId
 * @param {number} qty
 * @returns {{ packageId: BirthdayPackageId, qty: number, baseShares: number, buyerBonus: number, uplineBonus: number }|null}
 */
export function computeBirthdayBreakdown(packageId, qty) {
  if (!isBirthdayPackageId(packageId)) return null;
  const normQty = normalizeBirthdayQty(qty);
  if (normQty === null) return null;
  const pkg = BIRTHDAY_PROMO_CONFIG.packages[packageId];
  return {
    packageId,
    qty: normQty,
    baseShares: pkg.buyShares * normQty,
    buyerBonus: pkg.buyerBonus * normQty,
    uplineBonus: pkg.uplineBonus * normQty,
  };
}
